import { useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Building2, ShieldCheck } from "lucide-react";
import { BACKEND_URL } from "@/constants/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Modal } from "@/components/modals/Modal";

const nativeSelect =
  "h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm " +
  "ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 text-foreground";

export function CreateSchoolModal({ open, onClose }) {
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [sharingMode, setSharingMode] = useState("free");
  const [domainsInput, setDomainsInput] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [creating, setCreating] = useState(false);

  const handleClose = () => {
    setName("");
    setSharingMode("free");
    setDomainsInput("");
    setConfirmed(false);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !confirmed) return;

    const allowedDomains = domainsInput
      .split(",")
      .map((d) => d.trim().toLowerCase())
      .filter(Boolean);

    setCreating(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/schools`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: name.trim(), sharingMode, allowedDomains }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to create school");
      }
      const school = await res.json();
      toast.success("School created!");
      handleClose();
      navigate(`/schools/${school._id}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Create a School"
      subtitle="Invite teachers and manage class sharing centrally."
      icon={<Building2 size={16} className="text-primary" />}
      maxWidth="max-w-lg"
      preventClose={creating}
    >
      <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
        {/* School name */}
        <div className="space-y-1.5">
          <Label className="text-sm font-medium">
            School Name <span className="text-destructive/80 font-normal">*</span>
          </Label>
          <Input
            placeholder="e.g. Hillside Public School"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="h-10"
          />
        </div>

        {/* Sharing mode */}
        <div className="space-y-1.5">
          <Label className="text-sm font-medium">Class Sharing Mode</Label>
          <select
            value={sharingMode}
            onChange={(e) => setSharingMode(e.target.value)}
            className={nativeSelect}
          >
            <option value="free">Free - teachers share classes directly</option>
            <option value="restricted">Restricted - admin approves all share requests</option>
          </select>
          <p className="text-xs text-muted-foreground leading-relaxed">
            {sharingMode === "free"
              ? "Teachers can share classes with each other without approval."
              : "Every share request must be approved by you before access is granted."}
          </p>
        </div>

        {/* Domain restriction */}
        <div className="space-y-1.5">
          <Label className="text-sm font-medium">
            Allowed Email Domains{" "}
            <span className="text-muted-foreground font-normal text-xs">(optional)</span>
          </Label>
          <Input
            placeholder="e.g. tdsb.on.ca, ocdsb.ca"
            value={domainsInput}
            onChange={(e) => setDomainsInput(e.target.value)}
            className="h-10"
          />
          <p className="text-xs text-muted-foreground">
            Comma-separated. Leave blank to allow any email domain.
          </p>
        </div>

        {/* Admin confirmation */}
        <div className="rounded-xl border border-border bg-muted/30 px-4 py-3.5">
          <div className="flex items-start gap-3">
            <ShieldCheck size={15} className="text-primary mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground leading-snug">
                You will become the admin of this school
              </p>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                As admin, you are responsible for managing member access, sharing permissions,
                and class oversight. Make sure you are signed in with the correct account.
              </p>
              <label className="flex items-center gap-2 mt-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  className="w-4 h-4 rounded accent-primary"
                />
                <span className="text-xs text-foreground">
                  I confirm this is the correct account for admin access
                </span>
              </label>
            </div>
          </div>
        </div>

        <div className="flex gap-2 pt-1">
          <Button
            type="submit"
            disabled={creating || !name.trim() || !confirmed}
            className="flex-1"
          >
            {creating ? "Creating..." : "Create School"}
          </Button>
          <Button type="button" variant="outline" onClick={handleClose} disabled={creating}>
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
}
